import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  Divider,
  IconButton,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Delete } from '@mui/icons-material';
import { useParams } from 'react-router-dom';

import StateSelector from '../../../Components/StateSelector';
import CountrySelector from '../../../Components/CountrySelector';
import AlertItem from '../../../Components/AlertItem';
import { useApiSend } from '../../../utils/httpClientTs';
import {
  getChapterLookup,
  getChapterLookupInitial,
  createSeekingTransfer,
} from '../../../endpoints/seekingTransfer';
import {
  SeekingTransferChapter,
  SeekingTransferChapterLookupResponse,
  SeekingTransferCreate as SeekingTransferCreateType,
} from '../../../types/seekingTransfer';

const MILE_OPTIONS = ['15', '30', '50'] as const;
const CHAPTER_SIZE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'small', label: 'Small (25 and under members)' },
  { value: 'medium', label: 'Medium (26-50 members)' },
  { value: 'large', label: 'Large (51+ members)' },
] as const;

const CHAPTER_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'befbf9d24f254882b678609c86880cf9', label: 'In Person' },
  { value: '90aa631f3b714dd0b99c11593cf0af85', label: 'Hybrid' },
] as const;

export default function SeekingTransferCreate(): React.JSX.Element {
  const { userId } = useParams<{ userId: string }>();

  // State management
  const [countryId, setCountryId] = useState<string | null>(null);
  const [stateId, setStateId] = useState<string | null>(null);
  const [city, setCity] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');
  const [numberOfMiles, setNumberOfMiles] = useState<string>('15');
  const [chapterSize, setChapterSize] = useState<string>('all');
  const [chapterType, setChapterType] = useState<string>('all');
  const [errorLoading, setErrorLoading] = useState<boolean>(false);
  const [chapterInfo, setChapterInfo] = useState<
    SeekingTransferChapter[] | null
  >(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<string>('');
  const [chaptersSelected, setChaptersSelected] = useState<
    { id: string; name: string }[]
  >([]);
  const [note, setNote] = useState<string>('');
  const [savingRequest, setSavingRequest] = useState<boolean>(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState<boolean>(false);

  // API hooks
  const { mutate: getInfo, isPending } = useApiSend(
    getChapterLookup,
    (data: SeekingTransferChapterLookupResponse) => {
      setChapterInfo(data.chapters);
      setErrorLoading(false);
      setCountryId(data.country_id);
      setStateId(data.state_id);
      setCity(data.city);
      setPostalCode(data.postal_code ? data.postal_code.toString() : '');
      setNumberOfMiles(data.within ? data.within.toString() : '30');
      setIsUpdating(false);
    },
    (error: Error) => {
      setErrorLoading(true);
      console.error('Error loading transfer request info:', error);
    }
  );

  // API hooks
  const { mutate: getInitialInfo, isPending: isInitialLoading } = useApiSend(
    getChapterLookupInitial,
    (data: SeekingTransferChapterLookupResponse) => {
      setChapterInfo(data.chapters);
      setErrorLoading(false);
      setCountryId(data.country_id);
      setStateId(data.state_id);
      setCity(data.city);
      setPostalCode(data.postal_code ? data.postal_code.toString() : '');
      setNumberOfMiles(data.within ? data.within.toString() : '30');
      setIsUpdating(false);
      setInitialDataLoaded(true);
    },
    (error: Error) => {
      setInitialDataLoaded(true);
      setErrorLoading(true);
      console.error('Error loading transfer request info:', error);
    }
  );

  const { mutate: createRequest } = useApiSend(
    createSeekingTransfer,
    (data: { message: string }) => {
      setSavingRequest(false);
      window.location.href = `/seeking-transfer/${data.message}`;
    },
    (error: Error) => {
      setSavingRequest(false);
      setRequestError('Error creating transfer request. Please try again.');
      console.error('Error creating transfer request:', error);
    }
  );

  // Effects
  useEffect(() => {
    if (initialDataLoaded === false && isInitialLoading === false) {
      getInitialInfo({ userId: userId });
    }
  }, [initialDataLoaded, userId, getInitialInfo, isInitialLoading]);

  // Event handlers
  const handleGetNewInfo = useCallback(() => {
    if (!countryId) {
      setRequestError('Country is required');
      return;
    }
    if (!postalCode || postalCode.length < 3) {
      setRequestError('Postal Code must be at least 3 characters long');
      return;
    }
    if (!numberOfMiles) {
      setRequestError('Number of miles is required');
      return;
    }

    setRequestError('');
    setIsUpdating(true);

    const searchParams = {
      country_id: countryId,
      postal_code: postalCode,
      within: parseInt(numberOfMiles),
      chapter_size: chapterSize,
      chapter_type: chapterType,
    };

    getInfo(searchParams);
  }, [countryId, postalCode, numberOfMiles, chapterSize, chapterType, getInfo]);

  const addChapterToRequest = useCallback(
    (chapter: SeekingTransferChapter) => {
      const newChapter = {
        id: chapter.id,
        name: chapter.name,
      };

      if (!chaptersSelected.some((c) => c.id === chapter.id)) {
        setChaptersSelected((prev) => [...prev, newChapter]);
      }
    },
    [chaptersSelected]
  );

  const removeChapterFromRequest = useCallback(
    (chapter: SeekingTransferChapter | { id: string; name: string }) => {
      setChaptersSelected((prev) => prev.filter((c) => c.id !== chapter.id));
    },
    []
  );

  const createTransferRequest = useCallback(() => {
    if (chaptersSelected.length === 0) {
      setRequestError(
        'Please select at least one chapter for the transfer request.'
      );
      return;
    }

    setSavingRequest(true);
    setRequestError('');

    const chapterIds = chaptersSelected.map((c) => c.id);
    const requestData: SeekingTransferCreateType = {
      user_id: userId!,
      chapters: chapterIds,
      notes: note,
    };

    createRequest(requestData);
  }, [chaptersSelected, note, userId, createRequest]);

  const chapterInChapterSelected = useCallback(
    (chapter: SeekingTransferChapter) => {
      return chaptersSelected.some((c) => c.id === chapter.id);
    },
    [chaptersSelected]
  );

  // Loading states
  if (isPending || isInitialLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (errorLoading) {
    return (
      <Box sx={{ mt: 2 }}>
        <AlertItem
          severity="error"
          description="Error loading transfer request info"
          onClose={() => {}}
          keepOpen={true}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Header */}
        <Grid item xs={12}>
          <Typography variant="h4" align="center" gutterBottom>
            Seeking Transfer
          </Typography>
        </Grid>

        {/* Search Form */}
        <Grid item xs={12} md={6} lg={4}>
          <CountrySelector value={countryId} handleChange={setCountryId} />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <TextField
            size="small"
            fullWidth
            value={postalCode}
            label="Postal Code"
            onChange={(e) => setPostalCode(e.target.value)}
            error={!!requestError && !postalCode}
            helperText={
              requestError && !postalCode ? 'Postal Code is required' : ''
            }
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <FormControl fullWidth>
            <InputLabel id="miles-label">Miles</InputLabel>
            <Select
              size="small"
              label="Miles"
              labelId="miles-label"
              value={numberOfMiles}
              onChange={(e) => setNumberOfMiles(e.target.value)}
            >
              {MILE_OPTIONS.map((mile) => (
                <MenuItem key={mile} value={mile}>
                  {mile}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <FormControl fullWidth>
            <InputLabel id="chapter-size-label">Chapter Size</InputLabel>
            <Select
              size="small"
              label="Chapter Size"
              labelId="chapter-size-label"
              value={chapterSize}
              onChange={(e) => setChapterSize(e.target.value)}
            >
              {CHAPTER_SIZE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <FormControl fullWidth>
            <InputLabel id="chapter-type-label">Chapter Type</InputLabel>
            <Select
              size="small"
              label="Chapter Type"
              labelId="chapter-type-label"
              value={chapterType}
              onChange={(e) => setChapterType(e.target.value)}
            >
              {CHAPTER_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Conditional State and City fields */}
        {countryId && stateId && (
          <>
            <Grid item xs={12} md={6} lg={4}>
              <StateSelector
                value={stateId}
                handleChange={setStateId}
                countryId={countryId}
                disabled={true}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                value={city}
                label="City"
                fullWidth
                disabled={true}
                size="small"
              />
            </Grid>
          </>
        )}

        {/* Search Button */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={2}
          >
            <LoadingButton
              variant="contained"
              color="primary"
              size="small"
              onClick={handleGetNewInfo}
              loading={isUpdating}
              disabled={isUpdating}
            >
              Search Chapters
            </LoadingButton>
          </Box>
          {requestError && (
            <Box sx={{ mt: 1 }}>
              <AlertItem
                severity="error"
                description={requestError}
                onClose={() => setRequestError('')}
                keepOpen={true}
              />
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Selected Chapters Section */}
        {chaptersSelected.length > 0 && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6" align="center" gutterBottom>
                Chapters Selected for Transfer Request
              </Typography>

              <Box sx={{ mb: 2 }}>
                {chaptersSelected.map((chapter) => (
                  <Box
                    key={chapter.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      p: 1,
                      mb: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f5f5f5',
                    }}
                  >
                    <Typography variant="body1">{chapter.name}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeChapterFromRequest(chapter)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              <TextField
                fullWidth
                label="Notes (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                size="small"
                multiline
                rows={3}
                placeholder="Add any additional notes or requests for the transfer"
                sx={{ mb: 2, mt: 2 }}
              />

              <Box display="flex" justifyContent="flex-end">
                <LoadingButton
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={createTransferRequest}
                  loading={savingRequest}
                  disabled={savingRequest || chaptersSelected.length === 0}
                >
                  Submit Transfer Request
                </LoadingButton>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>
          </>
        )}

        {/* No Results Message */}
        {chapterInfo && chapterInfo.length === 0 && !isUpdating && (
          <Grid item xs={12}>
            <Alert severity="info">
              No chapters found for the specified criteria.
            </Alert>
          </Grid>
        )}

        {/* Updating Message */}
        {isUpdating && (
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={2}
            >
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography variant="body1">
                Updating chapter information...
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Chapter Results */}
        {chapterInfo && chapterInfo.length > 0 && !isUpdating && (
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Available Chapters
            </Typography>
            <Grid container spacing={2}>
              {chapterInfo.map((chapter) => (
                <Grid item xs={12} lg={6} key={chapter.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: 3,
                      borderRadius: 2,
                      backgroundColor: chapterInChapterSelected(chapter)
                        ? '#e0f7fa'
                        : '#fff',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <CardHeader
                      title={chapter.name}
                      subheader={`${chapter.distance} miles away`}
                      sx={{ pb: 1 }}
                    />
                    <CardContent sx={{ pt: 0, flexGrow: 1 }}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Active Member Count:</strong>{' '}
                          {chapter.member_count}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Type:</strong> {chapter.chapter_type}
                        </Typography>
                        {chapter.highlights && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Description:</strong> {chapter.highlights}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Meeting Times:</strong>
                        </Typography>
                        {chapter.meeting_info.map((meeting, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{ ml: 2, mb: 0.5, color: 'text.secondary' }}
                          >
                            {meeting.start_month === meeting.end_month
                              ? `${meeting.start_month} - ${meeting.frequency_timing} ${meeting.day_of_week} at ${meeting.time_of_day} ${meeting.midday}`
                              : `${meeting.start_month} thru ${meeting.end_month} - ${meeting.frequency} ${meeting.frequency_timing} ${meeting.day_of_week} at ${meeting.time_of_day} ${meeting.midday}`}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                    <CardActions>
                      {chapterInChapterSelected(chapter) ? (
                        <Button
                          variant="outlined"
                          size="small"
                          color="primary"
                          onClick={() => removeChapterFromRequest(chapter)}
                          fullWidth
                        >
                          Remove from Transfer Request
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          onClick={() => addChapterToRequest(chapter)}
                          fullWidth
                        >
                          Add to Transfer Request
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
